// StatusPill — a workflow status with its group colour (task 355.3). Replaces STATUS_COLORS tags.
//
// Group source: an explicit `group`, else resolveStatusGroup (api map per workflow state, then the client
// fallback). Unknown statuses render neutral (no data-group), never guessed.

import React from 'react';
import { resolveStatusGroup, statusLabel, type StatusGroup } from '../../theme/statusGroups';
import { useStatusGroupMap } from '../../theme/statusGroupsContext';
import styles from './StatusPill.module.css';

interface StatusPillProps {
  /**
   * Task 391: typed as possibly missing on purpose. A payload that lacks the field (api/IE drift) rendered
   * `undefined.replace` and took the whole route down — the Users page and the Integration dashboard both did.
   */
  status: string | null | undefined;
  workflowCode?: string | null;
  /** Overrides resolution (e.g. when the caller already has the api group). */
  group?: StatusGroup | null;
  /** Overrides the shell-provided api map (task 355.10), keyed `${workflowCode}:${stateCode}`. */
  groups?: ReadonlyMap<string, StatusGroup>;
  size?: 'default' | 'small';
}

export default function StatusPill({
  status,
  workflowCode,
  group,
  groups,
  size = 'default',
}: StatusPillProps): React.ReactElement {
  const apiGroups = useStatusGroupMap();
  const classes = [styles.pill, size === 'small' ? styles.small : ''].filter(Boolean).join(' ');

  if (!status) {
    // Nothing to show is a dash, not a crash.
    return (
      <span className={classes} title="No status">
        —
      </span>
    );
  }

  const resolved =
    group !== undefined ? group : resolveStatusGroup(status, workflowCode, groups ?? apiGroups);

  return (
    <span className={classes} data-group={resolved ?? undefined} title={status}>
      {statusLabel(status)}
    </span>
  );
}
